/**
 * The two per-request tags every RPC group that reaches a files writer or a
 * files read predicate must carry (spec R8, spec section 4.6).
 *
 * `AuditActor` (the ledger's, cova_audit P5a) says WHO IS ACTING and is read by
 * every writer statement, so a change cannot be recorded with a guessed actor or
 * with none. `FilesViewer` says WHO IS LOOKING and is read by
 * `FilesAuthorisationService`, so a predicate cannot quietly widen to the whole
 * firm. Both are read with a plain `yield*`, which is the guarantee: a runtime
 * that composes a files service without them does not compile (gate G8).
 *
 * THE ACTOR'S MIDDLEWARE IS THE LEDGER'S, `AuditActorMiddleware`, re-exported
 * here so a contract takes both tags from one module. The DMS's own
 * `ActivityActorMiddleware` is gone: it bound `{ kind: "user" }` for every call,
 * which the ledger's `user` arm now carries with the caller's organisation
 * (research 08 §2.2, the files row).
 *
 * ═══ WHY THIS IS TWO TAGS AND NOT ONE ═══
 *
 * `RpcMiddleware.Tag` cannot provide two: `TagClass.Provides` in `@effect/rpc`
 * reads a SINGLE `Context.Tag` off the options. The guarantee R8 actually wants
 * is that a runtime cannot supply one and forget the other, and that is kept a
 * different way: `activity-actor-middleware-live.ts` exports ONE
 * `FilesRequestMiddlewareLayer` that provides both implementations. A route
 * writes one layer line, a contract writes two `.middleware` calls, and a
 * contract that writes only one fails to compile the moment a handler reaches a
 * service that wants the other.
 *
 * The failure channel is `AuthorisationError` because the only way either of
 * these fails is that the caller has no identity or no membership, which is the
 * same sentence `UserMiddleware` already says.
 */

import { RpcMiddleware } from "@effect/rpc"
import { AuthorisationError } from "@/application/shared/schemas/authorisation-schemas"
import { FilesViewer } from "../domain/files-viewer"

export { AuditActorMiddleware } from "@/contexts/audit/ledger"

/**
 * Provides `FilesViewer`, with the caller's team ids and the once-per-request
 * memo of their grants.
 *
 * THE MEMO IS ON THE VALUE, NOT ON A SERVICE (spec section 4.3). The live layer
 * builds `accessSets` with `Effect.cached` over the grants read, so one request
 * resolves once however many predicates ask, and two requests never share an
 * answer, which is the one outcome a per-request tag exists to prevent.
 */
export class FilesViewerMiddleware extends RpcMiddleware.Tag<FilesViewerMiddleware>()(
  "Files/FilesViewerMiddleware",
  {
    provides: FilesViewer,
    failure: AuthorisationError,
  },
) {}
